/**
 * Role Constants
 * Centralized role names and utilities for consistent role checking
 */

export const ROLES = {
  ADMIN: 'admin',
  SUPER_ADMIN: 'super_admin',
  MANAGER: 'manager',
  TEAM_LEAD: 'team_lead',
  CLIENT: 'client',
  EMPLOYEE: 'employee',
} as const

export type RoleName = typeof ROLES[keyof typeof ROLES]

//TODO this is temporary, we need to get the super admin id from the database
export  const superUserId = "admin@dim.com";

/**
 * Role hierarchy levels (lower number = higher privilege)
 */
export const ROLE_HIERARCHY = {
  [ROLES.SUPER_ADMIN]: 0,
  [ROLES.ADMIN]: 1,
  [ROLES.MANAGER]: 2,
  [ROLES.TEAM_LEAD]: 3,
  [ROLES.EMPLOYEE]: 4,
  [ROLES.CLIENT]: 5,
} as const

/**
 * Role display names for UI
 */
export const ROLE_DISPLAY_NAMES = {
  [ROLES.ADMIN]: 'Admin',
  [ROLES.SUPER_ADMIN]: 'Super Admin',
  [ROLES.MANAGER]: 'Manager',
  [ROLES.TEAM_LEAD]: 'Team Lead',
  [ROLES.CLIENT]: 'Client',
  [ROLES.EMPLOYEE]: 'Employee',
} as const

/**
 * Utility functions for role checking
 */
export const RoleUtils = {
  /**
   * Check if user has a specific role
   */
  hasRole: (userRoles: { name: string }[], roleName: RoleName): boolean => {
    return userRoles.some(role => role.name === roleName)
  },

  /**
   * Check if user has admin privileges
   */
  isAdmin: (userRoles: { name: string }[]): boolean => {
    return RoleUtils.hasRole(userRoles, ROLES.ADMIN) || RoleUtils.hasRole(userRoles, ROLES.SUPER_ADMIN)
  },

  /**
   * Check if user has super admin privileges
   */
  isSuperAdmin: (userRoles: { name: string }[]): boolean => {
    return RoleUtils.hasRole(userRoles, ROLES.SUPER_ADMIN)
  },

  /**
   * Check if user has manager level privileges or higher
   */
  isManager: (userRoles: { name: string }[]): boolean => {
    return RoleUtils.hasRole(userRoles, ROLES.MANAGER) || RoleUtils.isAdmin(userRoles)
  },

  /**
   * Get the highest privilege role of a user
   */
  getHighestRole: (userRoles: { name: string }[]): RoleName | null => {
    const userRoleNames = userRoles.map(role => role.name as RoleName)
    let highestRole: RoleName | null = null
    let highestLevel = Infinity

    for (const roleName of userRoleNames) {
      const level = ROLE_HIERARCHY[roleName]
      if (level !== undefined && level < highestLevel) {
        highestLevel = level
        highestRole = roleName
      }
    }

    return highestRole
  },

  /**
   * Get display name for a role
   */
  getDisplayName: (roleName: RoleName): string => {
    return ROLE_DISPLAY_NAMES[roleName] || roleName
  },

  /**
   * Check if a role has higher or equal privileges than another role
   */
  hasHigherOrEqualPrivileges: (userRole: RoleName, requiredRole: RoleName): boolean => {
    const userLevel = ROLE_HIERARCHY[userRole]
    const requiredLevel = ROLE_HIERARCHY[requiredRole]
    return userLevel !== undefined && requiredLevel !== undefined && userLevel <= requiredLevel
  },
}

// Export commonly used constants for convenience
export { ROLES as USER_ROLES } 